'use client'

import { useEffect } from 'react'
import { useSession } from 'next-auth/react'

/**
 * Captures the browser's IANA time zone and persists it to the signed-in user's
 * record (so server-side daily-boundary logic uses the user's local "today", not
 * UTC). Mounted app-wide in the root layout (inside <Providers>); no-op while
 * logged out.
 *
 * The sync marker is keyed PER USER (`tz_synced:<userId>`), not per browser.
 * The old single `tz_synced` key caused a real bug: sign in as account A
 * (marker set), then switch to account B in the same browser → the POST never
 * fired → B's timezone stayed NULL forever, so B's "day" reset at UTC midnight
 * (8 PM ET) — a finished Daily Drill flipped back to "Start drill" mid-evening
 * (founder's test accounts, 2026-07-05).
 */
export default function TimezoneCapture() {
  const { data: session, status } = useSession()
  const userId = (session?.user as { id?: string } | undefined)?.id

  useEffect(() => {
    if (status !== 'authenticated' || !userId) return
    let tz: string | undefined
    try {
      tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    } catch {
      return
    }
    if (!tz) return
    const markerKey = `tz_synced:${userId}`
    try {
      if (localStorage.getItem(markerKey) === tz) return
    } catch {
      // localStorage unavailable (private mode) — fall through and POST.
    }
    fetch('/api/me/timezone', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      cache: 'no-store',
      body: JSON.stringify({ timezone: tz }),
    })
      .then((r) => {
        if (r.ok) {
          try {
            localStorage.setItem(markerKey, tz!)
          } catch {
            /* ignore */
          }
        }
      })
      .catch(() => {
        /* best-effort; will retry next mount */
      })
  }, [status, userId])

  return null
}
